import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Activity,
  GitCompare,
  GitMerge,
  BookOpen,
  ClipboardList,
  Database,
  History,
  LayoutDashboard,
  ListChecks,
  Loader2,
  MousePointerClick,
  Radar,
  Signpost,
  Timer,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

import ActionHistoryPanel from "./ActionHistoryPanel";
import ApplyChangesModal from "./ApplyChangesModal";
import ChangeAuditPanel from "./ChangeAuditPanel";
import CtaPanel from "./CtaPanel";
import DataLayersPanel from "./DataLayersPanel";
import { errorText, formatDateTime } from "./format";
import GuidesPanel from "./GuidesPanel";
import OverviewPanel from "./OverviewPanel";
import PipelinePanel from "./PipelinePanel";
import RegistryPanel from "./RegistryPanel";
import RuleClassifierPanel from "./RuleClassifierPanel";
import RunDiffPanel from "./RunDiffPanel";
import ConsolidatedSignalRegistry, {
  RegistrySignalDrawer,
} from "./SignalActionsPanel";
import SignalDetailDrawer from "./SignalDetailDrawer";
import TargetRegistryPanel from "./TargetRegistryPanel";
import TracesPanel from "./TracesPanel";
import {
  draftFromConfig,
  draftPayload,
  rebaseDraft,
  type ActionPlan,
  type OrgPresentationDraft,
  type PipelineHealth,
  type SignalAdminConfig,
  type SignalAdminConfigResponse,
  type SignalAdminDraft,
  type SignalAdminOrganization,
  type SignalAdminOrgPresentation,
  type SignalAdminOverview,
  type SignalAdminRegistry,
  type SignalAdminVersion,
} from "./types";
import Button from "../../Components/Button";
import { useAppDispatch } from "../../Store/Hooks";
import {
  applySignalAdminPlatformConfig,
  fetchSignalAdminOrgInspection,
  fetchSignalAdminOrganizations,
  fetchSignalPipelineHealth,
  fetchSignalAdminPlatformPolicy,
  saveSignalAdminActionPlanReviews,
} from "../../Store/Reducers/SignalAdminSlice";
import { toast } from "../../Utils/Helper/toast";

/**
 * Izba Admin → Config → Signal Administration.
 * Platform policy (Overview / Layers / Registry / CTAs / Audit) is one
 * global apply. Evaluation Traces and Action History are inspected per org.
 */

type AdminView =
  | "overview"
  | "signals"
  | "sweeper"
  | "layers"
  | "registry"
  | "targets"
  | "ctas"
  | "guides"
  | "classifier"
  | "traces"
  | "rundiff"
  | "history"
  | "audit";

const ORG_PARAM = "sa_org";
const VIEW_PARAM = "sa_view";
/** CTA Configuration narrowed to one target ("N CTA mappings →"). */
const CTA_TARGET_PARAM = "sa_target";
/** Action Guides editor open on one signal. */
const GUIDE_PARAM = "sa_guide";
const VIEW_IDS: AdminView[] = [
  "overview",
  "signals",
  "sweeper",
  "layers",
  "registry",
  "targets",
  "ctas",
  "guides",
  "classifier",
  "traces",
  "rundiff",
  "history",
  "audit",
];

const PLATFORM_VIEWS: AdminView[] = [
  "overview",
  "signals",
  "sweeper",
  "layers",
  "registry",
  "targets",
  "ctas",
  "guides",
  "classifier",
  "audit",
];
const INSPECT_VIEWS: AdminView[] = ["traces", "rundiff", "history"];

const DRAFT_KEY = "eb.signalAdmin.draft.v1";

/** A saved draft for `current`. One made against an older version is
 * replayed onto `current` (another admin saved in between) when it carries
 * the config it was made against; drafts saved before that was recorded
 * only restore onto their own version. */
const readSavedDraft = (
  current: SignalAdminConfigResponse
): { draft: SignalAdminDraft; rebased: boolean } | null => {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as {
      version: number;
      base?: SignalAdminConfig;
      draft: SignalAdminDraft;
    };
    // Drafts saved before tiers were configurable have no `tiers`.
    if (saved.version === current.version)
      return {
        draft: {
          ...saved.draft,
          tiers: saved.draft.tiers ?? { ...(current.config.tiers ?? {}) },
          classifier: saved.draft.classifier ?? {
            ...(current.config.classifier ?? {}),
          },
        },
        rebased: false,
      };
    if (saved.base && saved.version < current.version)
      return {
        draft: rebaseDraft(saved.base, saved.draft, current.config),
        rebased: true,
      };
    return null;
  } catch {
    return null;
  }
};

const saveDraft = (
  current: SignalAdminConfigResponse,
  draft: SignalAdminDraft | null
) => {
  try {
    if (draft)
      window.localStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({
          version: current.version,
          base: current.config,
          draft,
        })
      );
    else window.localStorage.removeItem(DRAFT_KEY);
  } catch {
    // Storage blocked: drafts just don't survive a refresh.
  }
};

const AdminSignalAdministrationContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [organizations, setOrganizations] = useState<SignalAdminOrganization[]>(
    []
  );
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgsError, setOrgsError] = useState("");
  const selectedOrgId = searchParams.get(ORG_PARAM) || "";
  const viewParam = searchParams.get(VIEW_PARAM) as AdminView | null;
  const view: AdminView =
    viewParam && VIEW_IDS.includes(viewParam) ? viewParam : "overview";
  const ctaTargetFilter = searchParams.get(CTA_TARGET_PARAM) || "";
  const editingGuideId = searchParams.get(GUIDE_PARAM) || "";

  const [current, setCurrent] = useState<SignalAdminConfigResponse | null>(
    null
  );
  const [draft, setDraft] = useState<SignalAdminDraft | null>(null);
  const [overview, setOverview] = useState<SignalAdminOverview | null>(null);
  const [orgPresentation, setOrgPresentation] =
    useState<SignalAdminOrgPresentation | null>(null);
  const [orgDraft, setOrgDraft] = useState<OrgPresentationDraft | null>(null);
  const [orgPresentationLoading, setOrgPresentationLoading] = useState(false);
  const [registry, setRegistry] = useState<SignalAdminRegistry | null>(null);
  const [versions, setVersions] = useState<SignalAdminVersion[]>([]);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policyError, setPolicyError] = useState("");
  const [openSignalId, setOpenSignalId] = useState<string | null>(null);
  /** The Registry page a Rule Classifier signal opens in the side pane. */
  const [classifierSignalId, setClassifierSignalId] = useState<string | null>(
    null
  );

  const [reviewOpen, setReviewOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState("");

  const setParam = useCallback(
    (key: string, value: string) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      });
    },
    [setSearchParams]
  );

  const setOrgId = useCallback(
    (orgId: string) => setParam(ORG_PARAM, orgId),
    [setParam]
  );

  // Pipeline health per organization: drives the Run Diff badge and banner.
  const [healthByOrg, setHealthByOrg] = useState<
    Record<string, PipelineHealth>
  >({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await dispatch(fetchSignalPipelineHealth()).unwrap();
        if (cancelled) return;
        const next: Record<string, PipelineHealth> = {};
        (res?.organizations ?? []).forEach(
          (o: { org_id: string; health: PipelineHealth }) => {
            next[o.org_id] = o.health;
          }
        );
        setHealthByOrg(next);
      } catch {
        // Health is advisory; the rest of the console works without it.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);
  const orgsNeedingAttention = Object.values(healthByOrg).filter(
    h => h.status !== "ok"
  ).length;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setOrgsLoading(true);
      setOrgsError("");
      try {
        const orgs = await dispatch(fetchSignalAdminOrganizations()).unwrap();
        if (!cancelled) setOrganizations(orgs ?? []);
      } catch (error) {
        if (!cancelled)
          setOrgsError(errorText(error, "Couldn't load organizations."));
      } finally {
        if (!cancelled) setOrgsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const loadPlatformPolicy = useCallback(
    async (orgIdForRegistry?: string) => {
      setPolicyLoading(true);
      setPolicyError("");
      try {
        // Without an organization the platform registry loads on its own
        // (no live counts), so these views never depend on picking one.
        const res = await dispatch(
          fetchSignalAdminPlatformPolicy(orgIdForRegistry)
        ).unwrap();
        const config = res.config as SignalAdminConfigResponse;
        setCurrent(config);
        // Unapplied changes survive a refresh, and another admin saving in
        // between: they are replayed onto the newer version.
        const saved = readSavedDraft(config);
        setDraft(saved?.draft ?? draftFromConfig(config.config));
        if (saved && draftPayload(config.config, saved.draft).count > 0)
          toast.success(
            saved.rebased
              ? "Kept your unapplied changes on top of newer saved settings."
              : "Restored your unapplied changes."
          );
        setVersions((res.versions ?? []) as SignalAdminVersion[]);
        if (res.registry) setRegistry(res.registry as SignalAdminRegistry);
      } catch (error) {
        setCurrent(null);
        setDraft(null);
        setPolicyError(
          errorText(error, "Couldn't load platform signal settings.")
        );
      } finally {
        setPolicyLoading(false);
      }
    },
    [dispatch]
  );

  const loadOrgInspection = useCallback(
    async (orgId: string) => {
      setOrgPresentationLoading(true);
      try {
        const res = await dispatch(
          fetchSignalAdminOrgInspection(orgId)
        ).unwrap();
        setOverview(res.overview as SignalAdminOverview);
        setRegistry(res.registry as SignalAdminRegistry);
        const presentation = res.presentation as SignalAdminOrgPresentation;
        setOrgPresentation(presentation);
        setOrgDraft({
          service_mode: presentation.config.service_mode,
          actions_surface: presentation.config.actions_surface,
        });
      } catch (error) {
        setOverview(null);
        setOrgPresentation(null);
        setOrgDraft(null);
        toast.error(
          errorText(error, "Couldn't load organization inspection data.")
        );
      } finally {
        setOrgPresentationLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (orgsLoading) return;
    void loadPlatformPolicy(selectedOrgId || undefined);
  }, [orgsLoading, loadPlatformPolicy, selectedOrgId]);

  useEffect(() => {
    setOverview(null);
    setOrgPresentation(null);
    setOrgDraft(null);
    setOpenSignalId(null);
    setClassifierSignalId(null);
    if (selectedOrgId) void loadOrgInspection(selectedOrgId);
  }, [selectedOrgId, loadOrgInspection]);

  const pending = useMemo(
    () =>
      current && draft
        ? draftPayload(current.config, draft)
        : { payload: {}, count: 0 },
    [current, draft]
  );

  // Keep unapplied changes across a refresh, and warn before leaving.
  useEffect(() => {
    if (!current || !draft) return;
    saveDraft(current, pending.count > 0 ? draft : null);
  }, [current, draft, pending.count]);
  useEffect(() => {
    if (pending.count === 0) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [pending.count]);

  /** After an apply or restore: say when cards will reflect it. */
  const appliedMessage = (
    version: number,
    reevaluation?: { evaluate?: boolean; public_data?: boolean }
  ) =>
    reevaluation?.evaluate || reevaluation?.public_data
      ? `Applied as version ${version}. Every account is being re-checked — cards update within a few minutes.`
      : `Applied as version ${version}. Cards show it on their next load.`;

  const applyDraft = async (reason: string) => {
    if (!current) return;
    setApplying(true);
    setApplyError("");
    try {
      const res = await dispatch(
        applySignalAdminPlatformConfig({
          payload: pending.payload as unknown as Record<string, unknown>,
          reason,
        })
      ).unwrap();
      saveDraft(current, null);
      setReviewOpen(false);
      toast.success(
        appliedMessage(
          res?.applied?.version ?? current.version + 1,
          res?.reevaluation
        )
      );
      await loadPlatformPolicy(selectedOrgId || undefined);
    } catch (error) {
      setApplyError(errorText(error, "Couldn't apply this change."));
    } finally {
      setApplying(false);
    }
  };

  /** Save changes-requested reviews for every admin to see. They change
   * nothing users see, so they skip Apply; the reload replays the rest of
   * this draft onto the new version. Rejects with a readable message. */
  const saveReviews = async (reviews: Record<string, ActionPlan>) => {
    const res = await dispatch(
      saveSignalAdminActionPlanReviews(
        Object.fromEntries(
          Object.entries(reviews).map(([id, plan]) => [
            id,
            {
              steps: plan.steps,
              rationale: plan.rationale,
              note: plan.note,
              awareness: plan.awareness === true,
            },
          ])
        )
      )
    ).unwrap();
    const count = Object.keys(reviews).length;
    toast.success(
      `${count === 1 ? "Review" : `${count} reviews`} saved as version ${
        res?.applied?.version ?? ""
      } — every admin can see ${count === 1 ? "it" : "them"} now.`
    );
    await loadPlatformPolicy(selectedOrgId || undefined);
  };

  const onRestored = async (
    version: number,
    reevaluation?: { evaluate?: boolean; public_data?: boolean }
  ) => {
    if (current) saveDraft(current, null);
    toast.success(appliedMessage(version, reevaluation));
    await loadPlatformPolicy(selectedOrgId || undefined);
  };

  const isInspectView = INSPECT_VIEWS.includes(view);
  const selectedOrg = organizations.find(o => o.org_id === selectedOrgId);

  const platformNav: {
    id: AdminView;
    label: string;
    icon: React.ReactNode;
    count?: number;
  }[] = [
    {
      id: "overview",
      label: "Overview",
      icon: <LayoutDashboard className="h-4 w-4" aria-hidden />,
    },
    {
      id: "signals",
      label: "Registry",
      icon: <ListChecks className="h-4 w-4" aria-hidden />,
      count: registry?.definitions.length,
    },
    {
      id: "sweeper",
      label: "Sweeper",
      icon: <Timer className="h-4 w-4" aria-hidden />,
    },
    {
      id: "layers",
      label: "Data Layers",
      icon: <Database className="h-4 w-4" aria-hidden />,
      count: registry?.layers.length,
    },
    {
      id: "registry",
      label: "Signal Registry",
      icon: <BookOpen className="h-4 w-4" aria-hidden />,
      count: registry?.definitions.length,
    },
    {
      id: "targets",
      label: "Target Registry",
      icon: <Signpost className="h-4 w-4" aria-hidden />,
      count: registry
        ? new Set([
            ...registry.target_registry.map(t => t.id),
            ...Object.keys(draft?.targets ?? {}),
          ]).size
        : undefined,
    },
    {
      id: "ctas",
      label: "CTA Configuration",
      icon: <MousePointerClick className="h-4 w-4" aria-hidden />,
    },
    {
      id: "guides",
      label: "Action Guides",
      icon: <ClipboardList className="h-4 w-4" aria-hidden />,
      count: draft
        ? Object.values(draft.guides).filter(g => g.live).length || undefined
        : undefined,
    },
    {
      id: "classifier",
      label: "Rule Classifier",
      icon: <GitMerge className="h-4 w-4" aria-hidden />,
      count: registry
        ? registry.classifier.rules.filter(r => r.status === "proposed")
            .length || undefined
        : undefined,
    },
    {
      id: "audit",
      label: "Change Audit",
      icon: <History className="h-4 w-4" aria-hidden />,
      count: versions.length || undefined,
    },
  ];

  const inspectNav: {
    id: AdminView;
    label: string;
    icon: React.ReactNode;
    count?: number;
    alert?: boolean;
  }[] = [
    {
      id: "traces",
      label: "Evaluation Traces",
      icon: <Activity className="h-4 w-4" aria-hidden />,
    },
    {
      id: "rundiff",
      label: "Run Diff",
      icon: <GitCompare className="h-4 w-4" aria-hidden />,
      count: orgsNeedingAttention || undefined,
      alert: orgsNeedingAttention > 0,
    },
    {
      id: "history",
      label: "Action History",
      icon: <ListChecks className="h-4 w-4" aria-hidden />,
    },
  ];

  const renderNavButton = (v: {
    id: AdminView;
    label: string;
    icon: React.ReactNode;
    count?: number;
    /** Count shown in red: something needs attention. */
    alert?: boolean;
  }) => (
    <button
      key={v.id}
      type="button"
      onClick={() =>
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          if (v.id === "overview") next.delete(VIEW_PARAM);
          else next.set(VIEW_PARAM, v.id);
          next.delete(CTA_TARGET_PARAM);
          next.delete(GUIDE_PARAM);
          return next;
        })
      }
      className={`flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-left text-[13px] ${
        view === v.id
          ? "bg-brand-tint font-bold text-brand-dark"
          : "text-gray-600 hover:bg-gray-100"
      }`}
    >
      {v.icon}
      {v.label}
      {v.count !== undefined && (
        <span
          title={
            v.alert
              ? "Organizations whose signal pipeline needs attention"
              : undefined
          }
          className={`ml-auto rounded-full px-1.5 text-[10.5px] font-semibold ${
            v.alert ? "bg-red-600 text-white" : "bg-white text-gray-500"
          }`}
        >
          {v.count}
        </span>
      )}
    </button>
  );

  return (
    <div className="flex flex-col">
      <header className="border-b border-gray-200 px-6 py-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-tint text-brand-dark">
            <Radar className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              Signal Administration
            </h2>
            <p className="mt-0.5 max-w-2xl text-[13px] text-gray-500">
              Platform-wide policy for how signals run and appear in every
              organization. Changes apply immediately everywhere and are
              recorded in Change Audit.
            </p>
          </div>
        </div>
      </header>

      {orgsError && (
        <div className="m-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
          {orgsError}
        </div>
      )}

      <div className="grid min-h-[480px] grid-cols-1 md:grid-cols-[228px_minmax(0,1fr)]">
        <nav
          aria-label="Signal Administration"
          className="flex gap-1 overflow-x-auto border-b border-gray-200 bg-gray-50/70 p-3 md:flex-col md:gap-0 md:border-b-0 md:border-r"
        >
          <div className="mb-1 hidden px-3 pt-1 md:block">
            <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-gray-400">
              Platform policy
            </span>
            <p className="mt-0.5 text-[11px] leading-snug text-gray-400">
              Applies to all organizations
            </p>
          </div>
          <div className="flex gap-1 md:mt-2 md:flex-col md:gap-0.5">
            {platformNav.map(renderNavButton)}
          </div>

          <div className="mx-2 my-3 hidden h-px bg-gray-200 md:block" />

          <div className="mb-1 hidden px-3 md:block">
            <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-gray-400">
              Inspect by organization
            </span>
            <p className="mt-0.5 text-[11px] leading-snug text-gray-400">
              Traces &amp; history only
            </p>
          </div>
          <div className="flex gap-1 md:mt-2 md:flex-col md:gap-0.5">
            {inspectNav.map(renderNavButton)}
          </div>
        </nav>

        <main className="min-w-0 p-5">
          {policyLoading && !current && (
            <div className="flex min-h-[240px] items-center justify-center text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
              <span className="ml-2 text-sm">Loading signal settings…</span>
            </div>
          )}

          {policyError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
              {policyError}
            </div>
          )}

          {current && draft && (
            <>
              {!isInspectView && (
                <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                  <span className="rounded-full bg-brand-tint px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-brand-dark">
                    All organizations
                  </span>
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-gray-600">
                    {current.version > 0 ? `v${current.version}` : "Default"}
                  </span>
                  <span>
                    {current.version > 0
                      ? `Applied ${formatDateTime(current.applied_at)}${current.applied_by_email ? ` by ${current.applied_by_email}` : ""}`
                      : "Never changed — running on the default configuration"}
                  </span>
                </div>
              )}

              {pending.count > 0 && PLATFORM_VIEWS.includes(view) && (
                <div className="sticky top-0 z-10 mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 shadow-sm">
                  <span className="text-[13px] font-semibold text-amber-900">
                    {pending.count} unapplied change
                    {pending.count === 1 ? "" : "s"}
                  </span>
                  <span className="text-xs text-amber-800">
                    Applying updates every organization at once.
                  </span>
                  <div className="ml-auto flex gap-2">
                    <Button
                      variant="white"
                      onClick={() => setDraft(draftFromConfig(current.config))}
                    >
                      Discard
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => {
                        setApplyError("");
                        setReviewOpen(true);
                      }}
                    >
                      Review and apply
                    </Button>
                  </div>
                </div>
              )}

              {view === "overview" && (
                <OverviewPanel
                  current={current}
                  draft={draft}
                  onDraftChange={setDraft}
                  overview={overview}
                  recentVersions={versions}
                  onOpenAudit={() => setParam(VIEW_PARAM, "audit")}
                  organizations={organizations}
                  orgsLoading={orgsLoading}
                  selectedOrgId={selectedOrgId}
                  selectedOrgName={selectedOrg?.org_name}
                  onOrgChange={setOrgId}
                  orgPresentation={orgPresentation}
                  orgDraft={orgDraft}
                  onOrgDraftChange={setOrgDraft}
                  orgPresentationLoading={orgPresentationLoading}
                  onOrgPresentationApplied={() => {
                    toast.success(
                      `Applied presentation for ${selectedOrg?.org_name || "organization"}.`
                    );
                    if (selectedOrgId) void loadOrgInspection(selectedOrgId);
                  }}
                />
              )}
              {view === "layers" && registry && (
                <DataLayersPanel
                  registry={registry}
                  draft={draft}
                  onDraftChange={setDraft}
                  onOpenSignal={setOpenSignalId}
                />
              )}
              {view === "registry" && registry && (
                <RegistryPanel
                  registry={registry}
                  draft={draft}
                  onOpenSignal={setOpenSignalId}
                />
              )}
              {view === "sweeper" && (
                <PipelinePanel
                  draft={draft}
                  appliedPipeline={current.config.pipeline ?? {}}
                  onDraftChange={setDraft}
                />
              )}
              {view === "signals" && registry && (
                <ConsolidatedSignalRegistry
                  registry={registry}
                  draft={draft}
                  appliedPlans={current.config.action_plans ?? {}}
                  onDraftChange={setDraft}
                  onSaveReviews={saveReviews}
                />
              )}
              {view === "targets" && registry && (
                <TargetRegistryPanel
                  registry={registry}
                  draft={draft}
                  appliedTargets={current.config.targets ?? {}}
                  onDraftChange={setDraft}
                  onOpenMappings={targetId =>
                    setSearchParams(prev => {
                      const next = new URLSearchParams(prev);
                      next.set(VIEW_PARAM, "ctas");
                      next.set(CTA_TARGET_PARAM, targetId);
                      return next;
                    })
                  }
                />
              )}
              {view === "ctas" && registry && (
                <CtaPanel
                  registry={registry}
                  draft={draft}
                  appliedCtas={current.config.ctas ?? {}}
                  onDraftChange={setDraft}
                  targetFilter={ctaTargetFilter}
                  onClearTargetFilter={() => setParam(CTA_TARGET_PARAM, "")}
                  onEditGuide={definitionId =>
                    setSearchParams(prev => {
                      const next = new URLSearchParams(prev);
                      next.set(VIEW_PARAM, "guides");
                      next.set(GUIDE_PARAM, definitionId);
                      return next;
                    })
                  }
                />
              )}
              {view === "guides" && registry && (
                <GuidesPanel
                  registry={registry}
                  draft={draft}
                  appliedGuides={current.config.guides ?? {}}
                  onDraftChange={setDraft}
                  editingId={editingGuideId}
                  onEdit={definitionId => setParam(GUIDE_PARAM, definitionId)}
                />
              )}
              {view === "classifier" && registry && (
                <RuleClassifierPanel
                  registry={registry}
                  config={current.config}
                  draft={draft}
                  onDraftChange={setDraft}
                  organizations={organizations}
                  orgsLoading={orgsLoading}
                  orgId={selectedOrgId}
                  onOrgChange={setOrgId}
                  onOpenSignal={setClassifierSignalId}
                />
              )}
              {view === "classifier" && registry && classifierSignalId && (
                <RegistrySignalDrawer
                  key={classifierSignalId}
                  signalId={classifierSignalId}
                  registry={registry}
                  draft={draft}
                  appliedPlans={current.config.action_plans ?? {}}
                  onDraftChange={setDraft}
                  onSaveReviews={saveReviews}
                  onClose={() => setClassifierSignalId(null)}
                  onOpenSignal={setClassifierSignalId}
                />
              )}
              {view === "traces" && (
                <TracesPanel
                  orgId={selectedOrgId}
                  organizations={organizations}
                  orgsLoading={orgsLoading}
                  onOrgChange={setOrgId}
                  registry={registry}
                  draft={draft}
                />
              )}
              {view === "rundiff" && (
                <RunDiffPanel
                  health={healthByOrg[selectedOrgId]}
                  orgId={selectedOrgId}
                  organizations={organizations}
                  orgsLoading={orgsLoading}
                  onOrgChange={setOrgId}
                />
              )}
              {view === "history" && (
                <ActionHistoryPanel
                  orgId={selectedOrgId}
                  organizations={organizations}
                  orgsLoading={orgsLoading}
                  onOrgChange={setOrgId}
                />
              )}
              {view === "audit" && (
                <ChangeAuditPanel
                  versions={versions}
                  loading={policyLoading}
                  currentVersion={current.version}
                  hasUnappliedChanges={pending.count > 0}
                  onRestored={onRestored}
                />
              )}

              {registry && openSignalId && (
                <SignalDetailDrawer
                  signalId={openSignalId}
                  registry={registry}
                  draft={draft}
                  onDraftChange={setDraft}
                  onClose={() => setOpenSignalId(null)}
                  onOpenSignal={setOpenSignalId}
                />
              )}

              <ApplyChangesModal
                isOpen={reviewOpen}
                onClose={() => setReviewOpen(false)}
                current={current}
                draft={draft}
                payload={pending.payload}
                overview={overview}
                registry={registry}
                applying={applying}
                error={applyError}
                onApply={reason => void applyDraft(reason)}
              />
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminSignalAdministrationContainer;
